import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Divider,
  Paper,
  Button,
  Chip,
  Stack,
} from "@mui/material";
import PaymentIcon from "@mui/icons-material/Payment";
import VerifiedIcon from "@mui/icons-material/Verified";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppTheme } from "../../../theme/useAppTheme";
import { useDispatch, useSelector } from "react-redux";
import ProductMediaThumb from "../ProductMediaPreview";
import useCurrency from "../../../hooks/useCurrency";

const getStatusColor = (status) => {
  switch (status) {
    case "PENDING":
      return "#ff9800";
    case "SUCCESS":
    case "APPROVED":
      return "#2e7d32";
    case "CANCELLED":
    case "REJECTED":
    case "RETURNED":
      return "#d32f2f";
    case "DISPATCHED":
    case "READYTODISPATCH":
      return "#6c63ff";
    default:
      return "#6c757d";
  }
};

const mapDeliveryStatus = (raw) => {
  switch (raw) {
    case "READYTODISPATCH":
      return "Ready To Dispatch";
    case "REFUNDCOMPLETED":
      return "Refund Completed";
    case "SUCCESS":
      return "Delivered";
    case "DISPATCHED":
      return "Dispatched";
    default:
      return raw || "Cancelled";
  }
};

export default function ProductOrderDetails() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const themeColors = useAppTheme();

  const { symbol } = useCurrency();

  if (state?.product && !state?.pendingOrder) {
    const product = state.product;
    const priceDetails = state.priceObj;

    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: themeColors.scaffoldGradientColors,
          border: `1px solid ${themeColors.scaffold}`,
          p: { xs: 2, md: 4 },
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 950,
            borderRadius: 4,
            boxShadow: 6,
            p: { xs: 2, sm: 3 },
            background: themeColors.bigContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              textAlign: "center",
              color: themeColors.primary,
              mb: 3,
            }}
          >
            {product.name}
          </Typography>
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: 4,
              background: themeColors.mediumContainer,
              border: `1px solid ${themeColors.scaffold}`,
              color: themeColors.text,
            }}
          >
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }} textAlign="center">
                  <ProductMediaThumb
                    urls={product.url || product.images}
                    videos={product.videos}
                    name={product.name}
                    width={160}
                    height={160}
                    borderRadius="20px"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography variant="h5" fontWeight={700}>
                    {product.name}
                  </Typography>
                  <Typography sx={{ mt: 1, fontSize: 15 }}>
                    {product.metalname || product.bullion?.metalName} •{" "}
                    {product.purity} • {product.metal_weight || product.metalWeight}{" "}
                    gms
                  </Typography>
                  {product.desc && (
                    <Typography sx={{ mt: 1, fontSize: 14 }}>
                      {product.desc}
                    </Typography>
                  )}
                  {priceDetails?.finalPrice != null && (
                    <Typography sx={{ mt: 2, fontWeight: 700, fontSize: 20 }}>
                      {symbol}{" "}
                      {Number(priceDetails.finalPrice).toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Card>
      </Box>
    );
  }

  if (!state?.pendingOrder || !state?.priceObj) {
    return (
      <Typography sx={{ p: 4, textAlign: "center" }}>
        No order details found!
      </Typography>
    );
  }

  const pendingOrder = state.pendingOrder;
  const priceDetails = state.priceObj;
  const steps = [
    {
      label: "Payment Status",
      icon: <PaymentIcon />,
      value: pendingOrder.transactions?.status || "CANCELLED",
    },
    {
      label: "Order Confirmation",
      icon: <VerifiedIcon />,
      value: pendingOrder.bookingstatus || "PENDING",
    },
    {
      label: "Delivery Status",
      icon: <LocalShippingIcon />,
      value: mapDeliveryStatus(pendingOrder.status),
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: themeColors.scaffoldGradientColors,
        border: `1px solid ${themeColors.scaffold}`,
        p: { xs: 2, md: 4 },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 950,
          borderRadius: 4,
          boxShadow: 6,
          p: 3,
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
          color: themeColors.text,
        }}
      >
        {/* PAGE HEADER */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            textAlign: "center",
            color: themeColors.primary,
            mb: 3,
          }}
        >
          Product Order Details
        </Typography>

        {/* PRODUCT INFO CARD */}
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: 4,
            background: themeColors.mediumContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
            mb: 3,
          }}
        >
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }} textAlign="center">
                <ProductMediaThumb
                  urls={
                    pendingOrder.product?.url || pendingOrder.product?.images
                  }
                  videos={pendingOrder.product?.videos}
                  name={pendingOrder.product?.name}
                  width={160}
                  height={160}
                  borderRadius="20px"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="h5" fontWeight={700}>
                  {pendingOrder.product.name}
                </Typography>

                <Typography sx={{ mt: 1, fontSize: 15 }}>
                  {pendingOrder.product.metalname} •{" "}
                  {pendingOrder.product.purity} •{" "}
                  {pendingOrder.product.metalWeight} gms
                </Typography>

                <Typography sx={{ mt: 1, fontSize: 14 }}>
                  {pendingOrder.product.desc}
                </Typography>

                {/* <Button
                  variant="contained"
                  sx={{
                    background: themeColors.primary,
                    color: themeColors.text,
                    border: `1px solid ${themeColors.scaffold}`,
                    mt: 2,
                    borderRadius: 3,
                    px: 3,
                    fontWeight: 600,
                  }}
                >
                  Download Invoice
                </Button> */}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* STATUS TRACKER */}
        <Card
          sx={{
            borderRadius: 4,
            p: 3,
            boxShadow: 4,
            mb: 3,
            background: themeColors.mediumContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
          }}
        >
          <Typography variant="h6" fontWeight={700} mb={2}>
            Order Status
          </Typography>

          <Stepper activeStep={-1} orientation="vertical">
            {steps.map((step, i) => (
              <Step key={i}>
                <StepLabel
                  icon={step.icon}
                  optional={
                    <Chip
                      label={step.value}
                      sx={{
                        mt: 1,
                        fontWeight: 600,
                        background: getStatusColor(step.value),
                        color: "#fff",
                      }}
                    />
                  }
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ color: themeColors.text }}
                  >
                    {step.label}
                  </Typography>
                </StepLabel>

                <Box
                  sx={{
                    ml: 7,
                    mt: 2,
                    mb: 2,
                    p: 2,
                    borderRadius: 3,
                    background: "#fafafa",
                    borderLeft: `6px solid ${getStatusColor(step.value)}`,
                  }}
                >
                  <Typography fontWeight={600}>
                    Status:{" "}
                    <span style={{ color: getStatusColor(step.value) }}>
                      {step.value}
                    </span>
                  </Typography>
                </Box>
              </Step>
            ))}
          </Stepper>
        </Card>

        {/* PRICE DETAILS */}
        <Card
          sx={{
            borderRadius: 4,
            p: 3,
            boxShadow: 4,
            background: themeColors.mediumContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
          }}
        >
          <Typography variant="h6" fontWeight={700} mb={2}>
            Price Summary
          </Typography>

          <Stack spacing={1.2}>
            <Typography fontSize={16}>
              <b>Product Price:</b> {symbol}{priceDetails.finalPrice}
            </Typography>
            <Typography fontSize={16}>
              <b>Tax:</b> {symbol}{priceDetails.taxAmount}
            </Typography>
            <Typography fontSize={16}>
              <b>Delivery Charges:</b> {symbol}{priceDetails.deliveryCharges || 0}
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Typography variant="h6" fontWeight={700}>
              Final Payable: {symbol}{priceDetails.finalPrice}
            </Typography>
          </Stack>
        </Card>
      </Card>
    </Box>
  );
}
